// ════════════════════════════════════════════════════════════════
// CRYPTO LAYER — Web Crypto API (SubtleCrypto)
// ════════════════════════════════════════════════════════════════
// Provides AES-256-GCM encryption for sensitive fields and
// PBKDF2 key derivation from a master passphrase.
//
// The session key lives in memory ONLY. When the PWA is closed
// or the tab is refreshed, the key is gone and the user must
// re-enter their passphrase. Data at rest in IndexedDB is
// always encrypted ciphertext.
// ════════════════════════════════════════════════════════════════

import { db } from './database';

const subtle = globalThis.crypto.subtle;
const PBKDF2_ITERATIONS = 600000; // OWASP 2023 recommendation
const SALT_LENGTH = 16;
const IV_LENGTH = 12;
const KEY_LENGTH = 256;

// ── Session key (lives in memory only) ──
let sessionKey: CryptoKey | null = null;

/**
 * Derives an AES-GCM key from a passphrase using PBKDF2.
 */
export async function deriveKey(
  passphrase: string,
  salt: Uint8Array
): Promise<CryptoKey> {
  const encoder = new TextEncoder();
  const keyMaterial = await subtle.importKey(
    'raw',
    encoder.encode(passphrase),
    'PBKDF2',
    false,
    ['deriveKey']
  );

  return subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt as BufferSource,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: KEY_LENGTH },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Unlocks the vault with a master passphrase.
 * Returns true if the passphrase is correct, false otherwise.
 * On first run, creates the encryption metadata.
 */
export async function unlock(passphrase: string): Promise<boolean> {
  const salt = await getOrCreateSalt();
  const key = await deriveKey(passphrase, salt);

  // Verify by attempting to decrypt the test ciphertext
  const testRecord = await db.appMeta.get('testCiphertext');
  if (testRecord) {
    try {
      await decryptString(key, testRecord.value);
    } catch {
      return false; // Wrong passphrase
    }
  } else {
    // First unlock — create a test ciphertext for future verification
    const encrypted = await encryptString(key, 'vault-unlock-verify');
    await db.appMeta.put({ key: 'testCiphertext', value: encrypted });
  }

  sessionKey = key;

  // Request persistent storage to reduce eviction risk
  if (navigator.storage && navigator.storage.persist) {
    navigator.storage.persist().catch(() => {});
  }

  return true;
}

/**
 * Checks if the vault has been initialized (first run detection).
 */
export async function isVaultInitialized(): Promise<boolean> {
  const testRecord = await db.appMeta.get('testCiphertext');
  return !!testRecord;
}

/**
 * Locks the vault by clearing the session key.
 */
export function lock(): void {
  sessionKey = null;
}

/**
 * Returns the current session key, or null if locked.
 */
export function getSessionKey(): CryptoKey | null {
  return sessionKey;
}

/**
 * Encrypts a plaintext string using AES-256-GCM.
 * Returns base64-encoded IV + ciphertext.
 */
export async function encryptString(
  key: CryptoKey,
  plaintext: string
): Promise<string> {
  const encoder = new TextEncoder();
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
  const ciphertext = await subtle.encrypt(
    { name: 'AES-GCM', iv: iv as BufferSource },
    key,
    encoder.encode(plaintext)
  );

  // Combine IV + ciphertext into a single buffer, then base64 encode
  const combined = new Uint8Array(iv.length + ciphertext.byteLength);
  combined.set(iv);
  combined.set(new Uint8Array(ciphertext), iv.length);

  return btoa(String.fromCharCode(...combined));
}

/**
 * Decrypts a base64-encoded AES-256-GCM ciphertext.
 */
export async function decryptString(
  key: CryptoKey,
  combined: string
): Promise<string> {
  const bytes = Uint8Array.from(atob(combined), (c) => c.charCodeAt(0));
  const iv = bytes.slice(0, IV_LENGTH);
  const ciphertext = bytes.slice(IV_LENGTH);
  const decoder = new TextDecoder();

  const plaintext = await subtle.decrypt(
    { name: 'AES-GCM', iv: iv as BufferSource },
    key,
    ciphertext as BufferSource
  );

  return decoder.decode(plaintext);
}

/**
 * Encrypts using the current session key (convenience wrapper).
 */
export async function encrypt(plaintext: string): Promise<string> {
  const key = getSessionKey();
  if (!key) throw new Error('Vault is locked. Cannot encrypt.');
  return encryptString(key, plaintext);
}

/**
 * Decrypts using the current session key (convenience wrapper).
 */
export async function decrypt(ciphertext: string): Promise<string> {
  const key = getSessionKey();
  if (!key) throw new Error('Vault is locked. Cannot decrypt.');
  return decryptString(key, ciphertext);
}

// ── Private helpers ──

async function getOrCreateSalt(): Promise<Uint8Array> {
  const existing = await db.appMeta.get('masterSalt');
  if (existing) {
    return Uint8Array.from(atob(existing.value), (c) => c.charCodeAt(0));
  }
  const salt = crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
  await db.appMeta.put({
    key: 'masterSalt',
    value: btoa(String.fromCharCode(...salt)),
  });
  return salt;
}
